import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/database';
import { TIER_LABELS, TIER_ORDER, INVITE_ELIGIBLE_TIERS } from '../../config/house.config';
import type { SupportedLang } from '../../config/house.config';

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const session = useStore(s => s.session);
  const getOverview = useStore(s => s.getOverview);

  if (!session?.member_id) return null;
  const lang = i18n.language as SupportedLang;
  const data = getOverview(session.member_id, lang);
  if (!data) return null;

  const { member, latestMsg, upcomingRel, liveReleases, archiveItems, events, invites } = data;
  const tierIdx = TIER_ORDER.indexOf(member.access_tier);
  const tr = (item: any) => item?.translations?.[lang] || item?.translations?.en || { title: '', content: '' };

  // CLICK MAP:
  // Member Card → /account
  // Latest Message → /messages
  // Upcoming Release → /releases/[slug]
  // Release cards → /releases/[slug]
  // Archive tiles → /archive/[slug] if unlocked, /membership if locked
  // Membership Ladder → /membership
  // Invites → /invite
  // Events → /events

  return (
    <div className="space-y-10">
      {/* Member Card */}
      <button onClick={() => navigate('/account')} className="w-full text-left group">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-widest-2xl uppercase text-charcoal/30 font-sans">{t('dashboard.welcome')}</p>
            <h1 className="font-serif text-2xl md:text-3xl mt-1 group-hover:text-charcoal/70 transition-colors">{member.first_name}</h1>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-widest uppercase text-gold font-sans">{TIER_LABELS[member.access_tier]}</p>
            <p className="text-[10px] text-charcoal/30 font-sans mt-1">{member.member_code}</p>
          </div>
        </div>
        <div className="w-full h-px bg-charcoal/5 mt-4" />
      </button>

      {/* Latest Message */}
      {latestMsg && (
        <button onClick={() => navigate('/messages')} className="w-full text-left group">
          <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans mb-2">{t('dashboard.latestMessage')}</p>
          <h2 className="font-serif text-xl group-hover:text-charcoal/70 transition-colors">{tr(latestMsg).title}</h2>
          <p className="text-sm text-charcoal/50 font-sans mt-1 line-clamp-2">{tr(latestMsg).content}</p>
        </button>
      )}

      {/* Upcoming Release */}
      {upcomingRel && (
        <button onClick={() => navigate(`/releases/${upcomingRel.slug}`)} className="w-full text-left group">
          <p className="text-[10px] tracking-widest-xl uppercase text-gold font-sans mb-2">{t('dashboard.upcomingRelease')}</p>
          <h2 className="font-serif text-xl group-hover:text-charcoal/70 transition-colors">{tr(upcomingRel).title}</h2>
          <p className="text-sm text-charcoal/50 font-sans mt-1 line-clamp-2">{tr(upcomingRel).content}</p>
        </button>
      )}

      {/* Live Releases */}
      {liveReleases.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans">{t('releases.title')}</p>
            <button onClick={() => navigate('/releases')} className="text-[10px] tracking-widest uppercase text-charcoal/30 hover:text-charcoal font-sans transition-colors">{t('dashboard.viewAll')} →</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {liveReleases.map(r => {
              const hasAccess = r.eligible_tiers.some((et: string) => TIER_ORDER.indexOf(et as any) <= tierIdx);
              return (
                <button key={r.id} onClick={() => navigate(`/releases/${r.slug}`)} className="text-left group border border-charcoal/5 p-5 hover:border-charcoal/15 transition-colors">
                  <p className="text-[10px] tracking-widest uppercase text-charcoal/20 font-sans mb-2">{r.house_category_slug}</p>
                  <h3 className="font-serif text-lg group-hover:text-charcoal/70 transition-colors">{tr(r).title}</h3>
                  {!hasAccess && <p className="text-[10px] text-gold font-sans mt-2">🔒 {t('releases.locked')}</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Archive Grid */}
      {archiveItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans">{t('dashboard.fromArchive')}</p>
            <button onClick={() => navigate('/archive')} className="text-[10px] tracking-widest uppercase text-charcoal/30 hover:text-charcoal font-sans transition-colors">{t('dashboard.viewAll')} →</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {archiveItems.map(a => {
              const unlocked = TIER_ORDER.indexOf(member.access_tier) >= TIER_ORDER.indexOf(a.min_tier);
              return (
                <button key={a.id} onClick={() => navigate(unlocked ? `/archive/${a.slug}` : '/membership')} className="text-left group border border-charcoal/5 p-4 hover:border-charcoal/15 transition-colors">
                  <h4 className="font-serif text-sm group-hover:text-charcoal/70 transition-colors">{tr(a).title}</h4>
                  {!unlocked && <p className="text-[10px] text-gold font-sans mt-1">🔒 {TIER_LABELS[a.min_tier]}</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Membership Ladder */}
      <button onClick={() => navigate('/membership')} className="w-full text-left group">
        <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans mb-3">{t('dashboard.yourMembership')}</p>
        <div className="border border-charcoal/5 p-5 group-hover:border-charcoal/15 transition-colors">
          <div className="flex items-center justify-between">
            <p className="font-serif text-lg">{TIER_LABELS[member.access_tier]}</p>
            <p className="text-sm font-sans text-charcoal/50">€{member.yearly_spend_eur.toLocaleString()}</p>
          </div>
        </div>
      </button>

      {/* Invites */}
      {INVITE_ELIGIBLE_TIERS.includes(member.access_tier) && (
        <button onClick={() => navigate('/invite')} className="w-full text-left group">
          <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans mb-2">{t('dashboard.inviteTitle')}</p>
          <p className="text-sm font-sans text-charcoal/50 group-hover:text-charcoal/70 transition-colors">
            {invites.length > 0 ? `${invites.filter((i: any) => i.status === 'active').length} active` : t('dashboard.viewDetails')}
          </p>
        </button>
      )}

      {/* Events */}
      {events.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-widest-xl uppercase text-charcoal/30 font-sans">{t('dashboard.eventsTitle')}</p>
            <button onClick={() => navigate('/events')} className="text-[10px] tracking-widest uppercase text-charcoal/30 hover:text-charcoal font-sans transition-colors">{t('dashboard.viewAll')} →</button>
          </div>
          {events.map((e: any) => (
            <button key={e.id} onClick={() => navigate('/events')} className="w-full text-left border border-charcoal/5 p-4 mb-2 hover:border-charcoal/15 transition-colors">
              <h4 className="font-serif">{tr(e).title}</h4>
              <p className="text-[10px] text-charcoal/40 font-sans mt-1">{new Date(e.event_date).toLocaleDateString()} · {e.location}</p>
            </button>
          ))}
        </div>
      )}

      {/* Fallback: never empty */}
      {!latestMsg && liveReleases.length === 0 && !upcomingRel && (
        <div className="text-center py-12 space-y-4">
          <p className="font-serif text-xl text-charcoal/50">{t('dashboard.noContent')}</p>
          <button onClick={() => navigate('/messages')} className="text-[10px] tracking-widest uppercase font-sans text-gold hover:text-charcoal transition-colors">
            {t('nav.messages')} →
          </button>
        </div>
      )}
    </div>
  );
}
